<template>
  <div class="relative" :class="[textfieldClasses, { 'show-active-state': showActiveState }]">
    <input
      :id="id"
      :ref="refName"
      v-model="content"
      :data-maska="mask"
      :class="`text-field-input ${hasIcon ? 'pr-56' : 'pr-16'}`"
      :type="type"
      :aria-label="ariaLabel"
      :aria-invalid="hasError"
      :aria-describedby="ariaDescribedByName"
      :required="required"
      :disabled="disabled"
      :readonly="readonly"
      :pattern="pattern"
      :maxlength="maxlength"
      :value="content"
      :inputmode="inputmode"
      :placeholder="placeholder"
      :name="name"
      :autocomplete="autocomplete"
      @blur="$emit('blur')"
      @keydown.enter="handleKeydownEnter"
    />
    <label :class="'pointer-events-none ' + textfieldLabelFilled" :for="id">{{ labelName }}</label>
    <div v-if="hasIcon" class="text-field-icon">
      <slot v-if="(!hasError && !disabled) || iconStatic" name="icon" />
    </div>
    <HelperText v-if="helperText" :id="id" :text="helperText" :status="status" />
  </div>
</template>

<script lang="ts">
import HelperText from '../helper_text/HelperText.vue';
import helperStatus from '../../helpers/status';

export default {
  name: 'TextField',
  components: {
    HelperText,
  },
  props: {
    mask: {
      type: [String, Boolean],
      required: false,
      default: false,
    },
    labelName: {
      type: String,
      required: true,
      validator(value: String) {
        return !!value;
      },
    },
    ariaLabel: {
      type: String,
      default: '',
      required: false,
    },
    autocomplete: {
      type: String,
      default: undefined,
      required: false,
    },
    id: {
      type: String,
      required: true,
      validator(value: String) {
        return !!value;
      },
    },
    type: {
      type: String,
      required: true,
      validator(value: string) {
        return ['text', 'number', 'email', 'tel', 'search', 'password'].indexOf(value) !== -1;
      },
    },
    name: {
      type: String,
      default: '',
      required: false,
    },
    status: {
      type: Number as () => helperStatus,
      default: helperStatus.Neutral,
    },
    value: {
      type: String,
      default: '',
      required: false,
    },
    placeholder: {
      type: String,
      default: '',
      required: false,
    },
    disabled: {
      type: Boolean,
      required: false,
    },
    required: {
      type: Boolean,
      required: false,
    },
    readonly: {
      type: Boolean,
      required: false,
    },
    inputmode: {
      type: String,
      default: 'text',
      required: false,
    },
    maxlength: {
      type: String,
      default: '',
      required: false,
    },
    pattern: {
      type: String,
      default: undefined,
      required: false,
    },
    helperText: {
      type: String,
      default: '',
      required: false,
    },
    iconStatic: {
      type: Boolean,
      default: false,
      required: false,
    },
    refName: {
      type: String,
      default: null,
      required: false,
    },
    showActiveState: {
      type: Boolean,
      default: false,
      required: false,
    },
  },
  data(): any {
    return {
      content: this.value,
    };
  },
  computed: {
    ariaDescribedByName(): String | null {
      return `${this.id}-helper`;
    },
    textfieldClasses(): String {
      if (this.hasError) {
        return 'text-field-error';
      }
      if (this.isValid) {
        return 'text-field-valid';
      }
      return 'text-field';
    },
    textfieldLabelFilled(): String {
      if (this.content) {
        return 'text-field-label-filled';
      }
      return 'text-field-label';
    },
    hasError(): Boolean {
      return this.status === helperStatus.Error;
    },
    isValid(): Boolean {
      return this.status === helperStatus.Valid;
    },
    hasIcon() {
      return this.$slots.icon;
    },
  },
  watch: {
    content(selectedValue: string) {
      if (this.pattern && new RegExp(this.pattern, 'g').test(selectedValue)) {
        this.content = this.content.match(this.pattern).join('');
      }
      this.$emit('input', this.content);
    },

    value(valueFromParent: String) {
      if (valueFromParent !== this.content) {
        this.content = valueFromParent;
      }
    },
  },
  methods: {
    handleKeydownEnter(event: KeyboardEvent) {
      event.preventDefault();
      if (event.key === 'Enter') {
        this.$emit('keydownEnter');
      }
    },
  },
};
</script>

<style scoped lang="scss">
.text-field-input {
  background-color: theme('colors.foreground');
  border: 1px solid theme('colors.grey.graphics.subdued');
  border-radius: theme('borderRadius.xl');
  color: theme('colors.grey.body');
  display: block;
  font-family: theme('fontFamily.sans');
  font-size: theme('fontSize.m');
  font-weight: theme('fontWeight.regular');
  height: 56px;
  padding: 1.5rem 0rem 0.5rem 1rem;
  letter-spacing: 0.09px;
  line-height: 1.5;
  position: relative;
  width: 100%;
  z-index: 0;
  &:focus {
    border: none;
    box-shadow: 0 0 0 2px theme('colors.grey.clickable');
    -webkit-appearance: none;
    outline: none;
  }
  &:disabled {
    background-color: theme('colors.grey.background.subdued');
    border: none;
    color: theme('colors.grey.text.subdued');
    cursor: not-allowed;
  }
}
.show-active-state .text-field-input:not(:focus) {
  @apply text-strong-body;
}
.text-field-error .text-field-input {
  border: none;
  -webkit-appearance: none;
  box-shadow: 0 0 0 2px theme('colors.critical.DEFAULT');
  border-radius: theme('borderRadius.xl');
}
.text-field-label {
  color: theme('colors.grey.text.subdued');
  font-family: theme('fontFamily.sans');
  font-size: theme('fontSize.m');
  font-weight: theme('fontWeight.regular');
  letter-spacing: 0.09px;
  position: absolute;
  left: 1rem;
  top: 1.125rem;
  transition:
    top 250ms cubic-bezier(0.4, 0, 0.2, 1),
    font-size 250ms cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1;
}
input:focus + .text-field-label {
  font-size: theme('fontSize.xs');
  top: 6px;
  transition:
    top 250ms cubic-bezier(0.4, 0, 0.2, 1),
    font-size 250ms cubic-bezier(0.4, 0, 0.2, 1);
}
.text-field-label-filled {
  color: theme('colors.grey.text.subdued');
  font-family: theme('fontFamily.sans');
  font-size: theme('fontSize.xs');
  font-weight: theme('fontWeight.regular');
  letter-spacing: 0.09px;
  position: absolute;
  left: 1rem;
  top: 6px;
  transition:
    top 150ms cubic-bezier(0.4, 0, 0.2, 1),
    font-size 150ms cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1;
}
.text-field-icon {
  position: absolute;
  left: calc(100% - 2rem);
  top: 18px;
  z-index: 2;
  &::after {
    content: '';
    display: block;
    background-color: theme('colors.foreground');
    height: 28px;
    width: 28px;
    position: absolute;
    top: 1px;
    z-index: -1;
    right: 8px;
  }
}
</style>
